/* eslint-disable no-inner-declarations */
import { useFlag } from '@unleash/proxy-client-react';
import { fetchAuthSession } from '@aws-amplify/auth';
import React from 'react';
import { useQuery } from 'react-query';
import { ReadyState } from 'react-use-websocket';
import { useWebSocket } from 'react-use-websocket/dist/lib/use-websocket';
import config from '../../../../../config';
import { SessionFieldsFragment, useGetOrganisationQuery } from '../../../../../generated/core-api/graphql-react-query';
import useAudioCapture from './audio/useAudioCapture';
import useLegacyAudioCapture from './audio/useLegacyAudioCapture';
import useSpeechRecognition from './audio/useSpeechRecognition';
import { AudioConfig } from '../../useAudioConfig';

interface DetectionMessage {
  type: 'detection';
  message: {
    id: string;
    cardId?: string;
    projectId: string;
    commandId: string;
    contextChange?: string;
  }
}

interface EndOfSessionMessage {
  type: 'endSessionAck'
}

interface StringMessage {
  type: undefined;
  message: string;
}

type AudioWebhookResponse = DetectionMessage | StringMessage | EndOfSessionMessage;

export default function useSessionAudio(audioConfig: AudioConfig, session?: SessionFieldsFragment | null, context?: string | null) {
  const useNewAudioEncoding = useGetOrganisationQuery().data?.org?.enableFeature_useNewAudioEncoding;
  const useAudioWebmEncoding = useFlag('use-audio-webm-opus-encoding') && !!useNewAudioEncoding;

  const lastTranscript = useSpeechRecognition(session?.project.asrLanguage);
  const [enabled, setEnabled] = React.useState<boolean>(false);
  const flag = useFlag('ws-auth');

  const userQuery = useQuery('cognito-user', () => fetchAuthSession());

  const {
    sendMessage: sendAudioMessage, sendJsonMessage, lastMessage, lastJsonMessage, readyState,
  } = useWebSocket<AudioWebhookResponse>(config.Live.AudioWebsocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => true,
    onError: () => console.error('Audio websocket connection error'),
    protocols: flag ? ['cogneed-ws-protocol', userQuery.data?.tokens?.idToken?.toString()] : undefined,
    onOpen: () => {
      if (session) {
        sendJsonMessage({
          type: 'settings',
          asrLanguage: session.project.asrLanguage,
          projectId: session.projectId,
          sessionId: session.id,
        });
      }
    },
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
  }, enabled);

  const lastAudioBlob = useAudioCapture(!!session && useAudioWebmEncoding && readyState === ReadyState.OPEN, audioConfig);
  const lastLegacyAudioBlob = useLegacyAudioCapture(!!session && !useAudioWebmEncoding, audioConfig);

  React.useEffect(() => {
    let isMounted = true;
    if (session) {
      setEnabled(true);
    } else {
      setTimeout(() => {
        if (isMounted) {
          setEnabled(false);
        }
      }, 200);
    }
    return () => {
      isMounted = false;
    };
  }, [session]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && session?.id) {
      sendJsonMessage({
        type: 'settings',
        asrLanguage: session.project.asrLanguage,
        projectId: session.projectId,
        sessionId: session.id,
      });
    }
  }, [session?.id, session?.projectId, session?.project.asrLanguage, readyState]);

  React.useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type === 'endSessionAck') {
      setEnabled(false);
    }
  }, [lastJsonMessage]);

  const stopSession = React.useCallback((endCause: string) => {
    sendJsonMessage({
      type: 'endSession',
      cause: endCause,
    });
  }, [sendJsonMessage]);

  React.useEffect(() => {
    if (session) {
      sendJsonMessage({
        type: 'settings',
        context: context || '',
      });
    }
  }, [context]);

  React.useEffect(() => {
    if (lastLegacyAudioBlob) {
      sendAudioMessage(JSON.stringify({
        type: 'audio',
        date: Date.now() * 1e6,
        data: lastLegacyAudioBlob,
      }));
    }
  }, [lastLegacyAudioBlob]);

  React.useEffect(() => {
    if (lastAudioBlob) {
      sendAudioMessage(JSON.stringify({
        type: 'audio',
        date: Date.now() * 1e6,
        webm: lastAudioBlob,
      }));
    }
  }, [lastAudioBlob]);

  React.useEffect(() => {
    if (lastTranscript) {
      sendJsonMessage(lastTranscript);
    }
  }, [lastTranscript]);

  return { stopSession, lastMessage };
}
